import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/Api";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";

const errorText = e => typeof e.response?.data === "string" ? e.response.data : e.response?.data?.mensaje || "No se pudo comunicar con el servidor.";
const utcDate = value => value ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`) : null;
const dateText = value => utcDate(value)?.toLocaleString() || "Sin reporte";

export default function TableroEfectores() {
  const { canEdit } = usePermisos();
  const editable = isSuperAdmin() || (canEdit("EFECTORES_EDIT") && canEdit("EFECTORES_TAB_TABLERO_EDIT"));
  const [ponds, setPonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updated, setUpdated] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState({});
  const [messages, setMessages] = useState({});
  const alive = useRef(false), revision = useRef(0), sending = useRef(new Set());

  const refresh = useCallback(async () => {
    const current = ++revision.current;
    try {
      // Estado operativo: consultar siempre al servidor, sin caché de navegación.
      const { data } = await api.get("/Efectores/todos");
      if (!Array.isArray(data)) throw new Error("Respuesta no válida");
      if (alive.current && current === revision.current) {
        setPonds(data); setError(""); setUpdated(new Date());
      }
    } catch (e) {
      if (alive.current && current === revision.current) setError(errorText(e));
    } finally {
      if (alive.current && current === revision.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    let stopped = false, timer;
    const poll = async () => {
      if (!document.hidden) await refresh();
      if (!stopped) timer = setTimeout(poll, 5000);
    };
    poll();
    const visible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", visible);
    return () => { stopped = true; alive.current = false; clearTimeout(timer); document.removeEventListener("visibilitychange", visible); };
  }, [refresh]);

  async function command(efector, encendido) {
    if (!editable || sending.current.has(efector.id)) return;
    sending.current.add(efector.id);
    setBusy(prev => ({ ...prev, [efector.id]: true }));
    setMessages(prev => ({ ...prev, [efector.id]: "Enviando orden…" }));
    try {
      const { data } = await api.put(`/Efectores/${efector.id}/encendido`, { encendido });
      if (alive.current) setMessages(prev => ({ ...prev, [efector.id]: data.mensaje || "Orden guardada. Esperando confirmación del ESP32." }));
      await refresh();
    } catch (e) {
      if (alive.current) setMessages(prev => ({ ...prev, [efector.id]: errorText(e) }));
    } finally {
      sending.current.delete(efector.id);
      if (alive.current) setBusy(prev => ({ ...prev, [efector.id]: false }));
    }
  }

  const normalized = query.trim().toLocaleLowerCase("es");
  const visible = ponds.map(pond => ({ ...pond, cards: (pond.dispositivos || []).flatMap(device =>
    (device.efectores || []).map(efector => ({ ...efector, device }))
  ).filter(e => [pond.estanqueNombre, e.nombre, e.device.dispositivoNombre, e.pinActivacion].some(x => String(x || "").toLocaleLowerCase("es").includes(normalized))) })).filter(p => p.cards.length);

  return <section aria-label="Tablero de efectores" className="space-y-5 min-w-0">
    <div className="rounded-xl border border-slate-600 bg-slate-800 p-4">
      <h2 className="text-xl font-semibold text-cyan-200">Tablero de control</h2>
      <p className="text-sm text-slate-300 mt-2">Control por estanque. Los indicadores muestran el último estado reportado por el ESP32; una orden enviada puede tardar en confirmarse.</p>
      <p className="text-xs text-slate-400 mt-2">Las automatizaciones configuradas siguen vigentes y pueden cambiar la orden. El reporte del GPIO no verifica por sí solo que la bomba esté funcionando.</p>
      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <input aria-label="Buscar efectores en el tablero" placeholder="Estanque, dispositivo o efector…" value={query} onChange={e => setQuery(e.target.value)} className="w-full min-w-0 rounded bg-slate-950 border border-slate-600 p-3" />
        <button type="button" onClick={refresh} className="shrink-0 rounded bg-cyan-800 px-4 py-3 hover:bg-cyan-700">Actualizar estados</button>
      </div>
      <p className="text-xs text-slate-400 mt-3">{updated ? `Última consulta: ${updated.toLocaleTimeString()}. Actualización cada 5 segundos.` : "Consultando estados…"}</p>
      {!editable && <p className="text-sm text-amber-200 mt-2">Solo lectura: no tienes permiso para enviar órdenes.</p>}
    </div>
    {error && <p role="alert" className="rounded border border-amber-600 bg-amber-950 p-3">{error} Los estados visibles pueden estar desactualizados.</p>}
    {loading && <p role="status">Cargando efectores…</p>}
    {!loading && !error && !visible.length && <p className="p-4 text-slate-300">{query ? "No hay efectores que coincidan con la búsqueda." : "No hay efectores disponibles. Puedes agregarlos en Crear Efector."}</p>}
    {visible.map(pond => <section key={pond.estanqueId} aria-label={`Estanque ${pond.estanqueNombre}`} className="rounded-xl border border-slate-600 bg-slate-900 p-3 sm:p-5">
      <div className="flex flex-wrap justify-between gap-2 mb-4"><h3 className="text-lg font-bold text-cyan-200 break-words min-w-0">{pond.estanqueNombre}</h3><span className="text-sm text-slate-400">{pond.cards.length} efectores</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pond.cards.map(e => {
          const known = typeof e.estadoConfirmado === "boolean";
          const pending = !known || e.estadoConfirmado !== e.encendido || (e.fechaOrdenUtc && (!e.fechaConfirmacionUtc || utcDate(e.fechaOrdenUtc) > utcDate(e.fechaConfirmacionUtc)));
          const recent = e.device.fechaUltimaConexion && Date.now() - utcDate(e.device.fechaUltimaConexion).getTime() < 90000;
          const disabled = !editable || !e.estaActivo || e.device.estaActivo === false || pond.estaActivo === false || busy[e.id] || !!error;
          return <article key={e.id} aria-label={e.nombre} className="min-w-0 rounded-lg border border-slate-600 bg-slate-800 p-4 shadow-lg break-words">
            <div className="flex items-start gap-3"><span aria-hidden="true" className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${!known ? "bg-amber-500 border-amber-200" : e.estadoConfirmado ? "bg-green-400 border-green-200 shadow-[0_0_12px_#4ade80]" : "bg-slate-500 border-slate-300"}`} /><div className="min-w-0"><h4 className="font-semibold text-white">{e.nombre}</h4><p className="text-xs text-slate-400">{e.device.dispositivoNombre} · {e.pinActivacion}</p></div></div>
            <p className="font-bold tracking-wide mt-4">{known ? (e.estadoConfirmado ? "ENCENDIDO" : "APAGADO") : "SIN CONFIRMACIÓN"}</p>
            <p className="text-xs text-slate-400 mt-1">Último reporte: {dateText(e.fechaConfirmacionUtc)}</p>
            <p className={`text-xs mt-2 ${recent ? "text-cyan-300" : "text-amber-200"}`}>{recent ? "Dispositivo con comunicación reciente" : "Sin comunicación reciente; el estado puede haber cambiado"}</p>
            <p className={`text-sm mt-3 ${pending ? "text-amber-200" : "text-slate-300"}`}>Orden: {e.encendido ? "encender" : "apagar"}{pending ? " · pendiente de confirmación" : " · confirmada"}</p>
            {(!e.estaActivo || e.device.estaActivo === false || pond.estaActivo === false) && <p className="text-sm text-amber-200 mt-2">Control deshabilitado: efector, dispositivo o estanque inactivo.</p>}
            {e.ultimoErrorDispositivo && <p className="text-sm text-red-300 mt-2">ESP32: {e.ultimoErrorDispositivo}</p>}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button type="button" disabled={disabled} onClick={() => command(e, true)} className="min-h-12 rounded border border-green-500 bg-green-800 py-3 font-semibold disabled:opacity-40 hover:bg-green-700">Encender</button>
              <button type="button" disabled={disabled} onClick={() => command(e, false)} className="min-h-12 rounded border border-red-400 bg-red-900 py-3 font-semibold disabled:opacity-40 hover:bg-red-800">Apagar</button>
            </div>
            {messages[e.id] && <p role="status" className="text-sm text-cyan-200 mt-3">{messages[e.id]}</p>}
          </article>;
        })}
      </div>
    </section>)}
  </section>;
}
